import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ItemCard } from '../../types/pointItemNote';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';

type Props = {
  open: boolean;
  items: ItemCard[];
  initialSelectedIds: string[];
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => void;
};

const AssignmentRewardItemPickModal: React.FC<Props> = ({
  open,
  items,
  initialSelectedIds,
  onClose,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(initialSelectedIds));
  }, [open, initialSelectedIds]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => a.name.localeCompare(b.name, 'ko')),
    [items]
  );

  if (!open) return null;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return createPortal(
    <div className="cp-apn-create-overlay" role="presentation">
      <div
        className="cp-apn-create-overlay-dim"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="cp-apn-create-overlay-scroll"
        role="presentation"
        onClick={onClose}
      >
        <div
          className="cp-apn-create-modal cp-apn-reward-pick-modal"
          role="dialog"
          aria-modal="true"
          aria-label="프로젝트 보상 아이템 선택"
          onClick={(e) => e.stopPropagation()}
        >
        <h2 className="cp-apn-create-title">획득 카드 설정</h2>
        <p className="cp-apn-reward-pick-desc">과제보상 아이템에서 지급할 카드를 선택하세요.</p>

        {sorted.length === 0 ? (
          <p className="cp-apn-empty-hint">
            등록된 과제보상 아이템이 없습니다. 포인트·아이템 노트에서 먼저 추가해 주세요.
          </p>
        ) : (
          <div className="cp-apn-reward-pick-grid">
            {sorted.map((item) => {
              const on = selected.has(item.itemCardId);
              return (
                <button
                  key={item.itemCardId}
                  type="button"
                  className={`cp-apn-reward-pick-card${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggle(item.itemCardId)}
                >
                  <span className="cp-apn-reward-pick-thumb" aria-hidden>
                    {resolveItemCardImageSrc(item.cardImage) ? (
                      <img src={resolveItemCardImageSrc(item.cardImage)!} alt="" />
                    ) : (
                      <span className="cp-apn-reward-pick-placeholder">카드</span>
                    )}
                  </span>
                  <span className="cp-apn-reward-pick-name">{item.name || '(이름 없음)'}</span>
                  {item.memo ? (
                    <span className="cp-apn-reward-pick-memo">{item.memo}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        <div className="cp-apn-create-buttons">
          <button type="button" className="cp-apn-create-btn-cancel" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="cp-apn-create-btn-save"
            onClick={() => onConfirm(Array.from(selected))}
          >
            확인 ({selected.size})
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssignmentRewardItemPickModal;
